import React from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import AssignmentReturnOutlinedIcon from "@mui/icons-material/AssignmentReturnOutlined";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import dayjs from "dayjs";
import { useTranslation } from "../../i18n/LanguageContext";

const STEP_ORDER = [
  "unprocessed",
  "dispatched",
  "received_by_customer",
  "returned_to_lab",
  "processing",
  "analysis_completed",
  "completed",
];

// A distinct icon per step so the row is legible on its own, not just via
// hover — completed steps still get the checkmark (see StepNode).
const STEP_ICONS = {
  unprocessed: ReceiptLongOutlinedIcon,
  dispatched: LocalShippingOutlinedIcon,
  received_by_customer: Inventory2OutlinedIcon,
  returned_to_lab: AssignmentReturnOutlinedIcon,
  processing: ScienceOutlinedIcon,
  analysis_completed: FactCheckOutlinedIcon,
  completed: CheckIcon,
};

// The single "which step is active right now" rule, shared by the notes
// block and the action chips below the timeline. A fully completed test has
// no "current" entry at all, so we fall back to the last completed step.
export function resolveCurrentStep(timeline) {
  if (!Array.isArray(timeline) || timeline.length === 0) return null;
  const current = timeline.find((s) => s.state === "current");
  if (current) return current;
  return [...timeline].reverse().find((s) => s.state === "completed") || null;
}

// Raw step_key with underscores turned into spaces - used in the admin
// view, which wants the actual technical status name instead of the
// patient-facing copy (e.g. "received by customer", not "Kit delivered").
function rawLabel(stepKey) {
  return stepKey.replace(/_/g, " ");
}

// Tooltip always names the step, not just a bare date — a fully completed
// timeline has 7 identical-looking blue checkmarks, so the date alone
// doesn't say which step it belongs to.
function formatTooltip(step, rawLabels, t) {
  const label = rawLabels ? rawLabel(step.step_key) : t(`portal.statusTimeline.steps.${step.step_key}.label`);
  if (step.state === "completed" && step.completed_at) {
    return t("portal.statusTimeline.tooltip.completed", {
      label,
      date: dayjs(step.completed_at).format("MMM D, YYYY [at] HH:mm"),
    });
  }
  if (step.state === "current" && step.started_at) {
    return t("portal.statusTimeline.tooltip.inProgress", {
      label,
      date: dayjs(step.started_at).format("MMM D, YYYY"),
    });
  }
  return null;
}

function StepNode({ step, rawLabels }) {
  const { t } = useTranslation();
  const isCompleted = step.state === "completed";
  const isCurrent = step.state === "current";
  const tooltip = formatTooltip(step, rawLabels, t);
  const StepIcon = STEP_ICONS[step.step_key] || ReceiptLongOutlinedIcon;

  const node = (
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        bgcolor: isCompleted ? "#0058a5" : "transparent",
        border: isCompleted ? "none" : isCurrent ? "2px solid #0058a5" : "2px solid #d1d5db",
        color: isCompleted ? "#fff" : isCurrent ? "#0058a5" : "#9ca3af",
        transition: "background-color 0.35s ease, border-color 0.35s ease, color 0.35s ease",
        "@keyframes portalStepPulse": {
          "0%": { boxShadow: "0 0 0 0 rgba(0,88,165,0.45)" },
          "70%": { boxShadow: "0 0 0 7px rgba(0,88,165,0)" },
          "100%": { boxShadow: "0 0 0 0 rgba(0,88,165,0)" },
        },
        animation: isCurrent ? "portalStepPulse 1.8s ease-out infinite" : "none",
      }}
    >
      {isCompleted ? <CheckIcon sx={{ fontSize: 16 }} /> : <StepIcon sx={{ fontSize: 15 }} />}
    </Box>
  );

  if (!tooltip) return node;
  return (
    <Tooltip title={tooltip} arrow>
      {node}
    </Tooltip>
  );
}

// The horizontal 7-step order-status row for a single test. `state` on each
// entry ("completed" | "current" | "upcoming") drives all styling — the
// backend sends no display text, see portal.statusTimeline in the i18n
// dictionaries. Each node's step name is always shown underneath (not only
// on hover), so a fully completed row (7 identical checkmarks) still reads
// which step is which without relying on the tooltip.
function StatusTimeline({ timeline, rawLabels = false }) {
  const { t } = useTranslation();
  if (!Array.isArray(timeline) || timeline.length === 0) return null;

  const byKey = Object.fromEntries(timeline.map((s) => [s.step_key, s]));
  const steps = STEP_ORDER.map((key) => byKey[key]).filter(Boolean);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        width: "100%",
        py: 1,
        overflowX: "auto",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        "&::-webkit-scrollbar": { display: "none" },
      }}
    >
      {steps.map((step, idx) => {
        const nextStep = steps[idx + 1];
        // Line leading into the next node is colored once that next step has
        // been reached (completed or current), grey while still upcoming.
        const lineActive = nextStep && nextStep.state !== "upcoming";
        return (
          <React.Fragment key={step.step_key}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0, width: 68 }}>
              <StepNode step={step} rawLabels={rawLabels} />
              <Typography
                sx={{
                  mt: 0.5,
                  fontSize: "0.625rem",
                  lineHeight: 1.25,
                  textAlign: "center",
                  color: step.state === "upcoming" ? "#9ca3af" : "#374151",
                  fontWeight: step.state === "upcoming" ? 400 : 600,
                }}
              >
                {rawLabels ? rawLabel(step.step_key) : t(`portal.statusTimeline.steps.${step.step_key}.label`)}
              </Typography>
            </Box>
            {nextStep && (
              <Box
                sx={{
                  flex: 1,
                  minWidth: 16,
                  height: 2,
                  mt: "13px",
                  bgcolor: lineActive ? "#0058a5" : "#e5e7eb",
                  transition: "background-color 0.35s ease",
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
}

export default StatusTimeline;
